import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type CCPARequest = Tables<"ccpa_requests">;

interface CCPARequestCardProps {
  request: CCPARequest;
  isOpen: boolean;
  onClose: () => void;
}

const CCPARequestCard = ({ request, isOpen, onClose }: CCPARequestCardProps) => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    try {
      const { error } = await supabase.functions.invoke('send-ccpa-request-email', {
        body: {
          recipient: emailAddress,
          record: request
        }
      });

      if (error) throw error;

      toast({
        title: "Email Sent",
        description: "CCPA request details have been sent successfully.",
      });
      setEmailDialogOpen(false);
      setEmailAddress("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={onClose}>
        <AlertDialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
          
          <AlertDialogHeader>
            <AlertDialogTitle>CCPA Request Details</AlertDialogTitle>
            <AlertDialogDescription>
              View and manage CCPA request information
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setEmailDialogOpen(true)}
              variant="outline"
            >
              Email Request Details
            </Button>
          </div>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <div className="font-medium">Name</div>
                <div>{request.name}</div>
              </div>
              <div>
                <div className="font-medium">Email</div>
                <div>{request.email}</div>
              </div>
              <div>
                <div className="font-medium">Phone</div>
                <div>{request.phone || "Not provided"}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <div className="font-medium">Request Type</div>
                <div className="capitalize">{request.request_type.replace(/_/g, " ")}</div>
              </div>
              <div>
                <div className="font-medium">Data Categories</div>
                <div>{request.data_categories.join(", ")}</div>
              </div>
              <div>
                <div className="font-medium">Verification Method</div>
                <div className="capitalize">{request.verification_method.replace(/_/g, " ")}</div>
              </div>
              <div>
                <div className="font-medium">Status</div>
                <div className="capitalize">{request.status}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <div className="font-medium">Source</div>
                <div className="capitalize">{request.source.replace(/_/g, " ")}</div>
              </div>
              <div>
                <div className="font-medium">Created</div>
                <div>{format(new Date(request.created_at), "MMMM d, yyyy")}</div>
              </div>
              {request.processed_at && (
                <div>
                  <div className="font-medium">Processed</div>
                  <div>{format(new Date(request.processed_at), "MMMM d, yyyy")}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send CCPA Request Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="Enter email address"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEmailDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSending}>
                {isSending ? "Sending..." : "Send"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CCPARequestCard;